import type { AppType } from "@dantria/api/src/index";
import { percentToRate } from "@dantria/core";
import { hc } from "hono/client";

/**
 * Typed RPC client. `AppType` is the Hono route table from apps/api, so a route
 * signature change breaks the call site at compile time instead of at runtime.
 */
export const api = hc<AppType>("/", {
  init: { credentials: "include" },
});

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * `lines.0.sellBasis` → `Line 1 · sellBasis`. A numeric segment names a row
 * in an array the caller submitted, one-based to match what a person counts;
 * the noun before it (singularized) says which array.
 */
function friendlyPath(path: string): string {
  const parts = path.split(".");
  const out: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!;
    if (i > 0 && /^\d+$/.test(p)) {
      const noun = parts[i - 1]!.replace(/s$/, "");
      out[out.length - 1] = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} ${Number(p) + 1}`;
    } else {
      out.push(p);
    }
  }
  return out.join(" · ");
}

/**
 * A readable message for a caught error, field detail included.
 *
 * `unwrap`'s `ApiError` carries per-field messages on a 422, keyed by the
 * schema path that failed (`zodBody` in the API — see `apps/api/src/validate.ts`).
 * A form built on `useResource` shows those inline against the field that
 * caused them; a form that instead uses a bare `useMutation` — a whole-set
 * replace with rows the schema addresses by array index, where there is no
 * one input to point at — has nowhere to put that, and left alone, the
 * user sees only the generic "check the highlighted fields" with nothing
 * highlighted. This is the fallback for exactly that case.
 */
export function apiErrorMessage(
  err: unknown,
  fallback = "Something went wrong. Try again.",
): string {
  if (err instanceof ApiError) {
    if (err.fields && Object.keys(err.fields).length > 0) {
      return Object.entries(err.fields)
        .map(([path, message]) => `${friendlyPath(path)}: ${message}`)
        .join("; ");
    }
    return err.message;
  }
  return fallback;
}

type JsonOf<R> = R extends { json: () => Promise<infer T> } ? T : never;

/**
 * A Hono response type is a union of the success body and every error body a
 * route can return. Since `unwrap` throws on the error branches, the value it
 * resolves to is the success body alone — so those branches are excluded here
 * rather than leaking `{ error: string }` into every component.
 */
type SuccessBody<R> = Exclude<JsonOf<R>, { error: string }>;

/**
 * Unwrap an RPC response, throwing a typed error on failure.
 *
 * The payload type flows from the route definition, so call sites never restate
 * the API shape — a field renamed in a handler breaks the component that reads
 * it, at compile time.
 *
 * It takes the promise as well as the response, and awaits it here. Taking only
 * a `Response` made `unwrap(api.thing.$post(...))` a type error, which call
 * sites silenced with `as never` — and an un-awaited promise has no `.ok`, so
 * every one of them reported a failure on a request the server had accepted.
 * Accepting both removes the reason to reach for the cast.
 */
export async function unwrap<R extends HttpResponse>(
  response: R | Promise<R>,
): Promise<SuccessBody<R>> {
  const res = await response;
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      fields?: Record<string, string>;
    };
    throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status, body.fields);
  }
  return (await res.json()) as SuccessBody<R>;
}

interface HttpResponse {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}

/**
 * Amounts arrive as decimal strings and must stay that way. Formatting for
 * display is the only place they may become anything else, and even here the
 * value is parsed once, at the edge, purely to drive Intl.
 */
export function formatMoney(amount: string, currency: string, locale = "en-US"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(amount));
}

/**
 * Rates travel as 0–1 because that is what they are multiplied by; nobody
 * reads or types them that way. `formatPercent` comes straight from
 * @dantria/core so a rate is rendered by the same code that stores one.
 */
export { formatPercent, rateToPercent } from "@dantria/core";

/**
 * A percentage typed into a form, as the rate the API expects.
 *
 * Lenient on purpose. Blank means zero — an untouched retainage field is not
 * an error. Anything that isn't a number is passed through untouched so the
 * API's own validator produces the field error, rather than this quietly
 * turning "abou 5" into something and submitting it.
 */
export function rateFromPercent(percent: string): string {
  const v = percent.trim();
  if (!v) return "0";
  try {
    return percentToRate(v);
  } catch {
    return v;
  }
}

/**
 * Group a decimal amount for display without ever turning it into a number.
 *
 * The one formatter every table uses. `currency` appends the code, and is left
 * off where a column header or a stat card already says which currency it is.
 */
export function fmtAmount(amount: string, currency?: string): string {
  const [whole, cents = "00"] = amount.split(".");
  const neg = whole?.startsWith("-");
  const digits = (neg ? whole!.slice(1) : (whole ?? "0")).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${neg ? "-" : ""}${digits}.${cents.padEnd(2, "0")}${currency ? ` ${currency}` : ""}`;
}
